/**
 * Plantillas mínimas para crear archivos ofimáticos vacíos desde el navegador.
 *
 * Para .xlsx delegamos en SheetJS (ya disponible en utils/univerAdapter).
 * Para .docx empaquetamos en cliente el ZIP mínimo de OpenXML (sin dependencias externas).
 */

import { createEmptyXlsxBytes } from './univerAdapter';

export { createEmptyXlsxBytes };

/**
 * Genera un .docx mínimo válido (OOXML) usando un único documento con un párrafo vacío.
 *
 * OOXML = ZIP con estos entries:
 *   [Content_Types].xml
 *   _rels/.rels
 *   word/document.xml
 *
 * Implementamos un empaquetador ZIP STORE (sin compresión) en ~60 líneas para
 * evitar añadir JSZip al bundle. Suficiente para ficheros < 1 KB.
 */
export function createEmptyDocxBytes() {
  const files = [
    {
      path: '[Content_Types].xml',
      content:
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>' +
        '</Types>',
    },
    {
      path: '_rels/.rels',
      content:
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>' +
        '</Relationships>',
    },
    {
      path: 'word/document.xml',
      content:
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">' +
        '<w:body><w:p><w:r><w:t xml:space="preserve"></w:t></w:r></w:p></w:body>' +
        '</w:document>',
    },
  ];

  return buildZipStore(files);
}

/* -------------------- ZIP builder (método STORE, sin compresión) -------------------- */

function buildZipStore(files) {
  const encoder = new TextEncoder();
  const encoded = files.map((f) => ({
    path: f.path,
    nameBytes: encoder.encode(f.path),
    dataBytes: encoder.encode(f.content),
  }));

  // CRC-32 según ZIP spec
  const crcTable = (() => {
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      t[n] = c;
    }
    return t;
  })();
  const crc32 = (buf) => {
    let c = 0 ^ -1;
    for (let i = 0; i < buf.length; i++) c = (c >>> 8) ^ crcTable[(c ^ buf[i]) & 0xff];
    return (c ^ -1) >>> 0;
  };

  const localParts = [];
  const centralParts = [];
  let offset = 0;

  for (const f of encoded) {
    const crc = crc32(f.dataBytes);
    const size = f.dataBytes.length;
    const nameLen = f.nameBytes.length;

    // Local file header
    const lfh = new Uint8Array(30 + nameLen);
    const dv = new DataView(lfh.buffer);
    dv.setUint32(0, 0x04034b50, true);
    dv.setUint16(4, 20, true); // version needed
    dv.setUint16(6, 0, true); // flags
    dv.setUint16(8, 0, true); // method = 0 (STORE)
    dv.setUint16(10, 0, true); // time
    dv.setUint16(12, 0, true); // date
    dv.setUint32(14, crc, true);
    dv.setUint32(18, size, true);
    dv.setUint32(22, size, true);
    dv.setUint16(26, nameLen, true);
    dv.setUint16(28, 0, true);
    lfh.set(f.nameBytes, 30);

    localParts.push(lfh, f.dataBytes);
    const localHeaderOffset = offset;
    offset += lfh.length + size;

    // Central directory header
    const cdh = new Uint8Array(46 + nameLen);
    const dv2 = new DataView(cdh.buffer);
    dv2.setUint32(0, 0x02014b50, true);
    dv2.setUint16(4, 20, true); // version made by
    dv2.setUint16(6, 20, true); // version needed
    dv2.setUint16(8, 0, true);
    dv2.setUint16(10, 0, true);
    dv2.setUint16(12, 0, true);
    dv2.setUint16(14, 0, true);
    dv2.setUint32(16, crc, true);
    dv2.setUint32(20, size, true);
    dv2.setUint32(24, size, true);
    dv2.setUint16(28, nameLen, true);
    dv2.setUint16(30, 0, true);
    dv2.setUint16(32, 0, true);
    dv2.setUint16(34, 0, true);
    dv2.setUint16(36, 0, true);
    dv2.setUint32(38, 0, true);
    dv2.setUint32(42, localHeaderOffset, true);
    cdh.set(f.nameBytes, 46);
    centralParts.push(cdh);
  }

  const centralSize = centralParts.reduce((a, b) => a + b.length, 0);
  const centralOffset = offset;

  // End of central directory
  const eocd = new Uint8Array(22);
  const dv3 = new DataView(eocd.buffer);
  dv3.setUint32(0, 0x06054b50, true);
  dv3.setUint16(4, 0, true);
  dv3.setUint16(6, 0, true);
  dv3.setUint16(8, encoded.length, true);
  dv3.setUint16(10, encoded.length, true);
  dv3.setUint32(12, centralSize, true);
  dv3.setUint32(16, centralOffset, true);
  dv3.setUint16(20, 0, true);

  const totalLen = offset + centralSize + eocd.length;
  const out = new Uint8Array(totalLen);
  let p = 0;
  for (const part of localParts) {
    out.set(part, p);
    p += part.length;
  }
  for (const part of centralParts) {
    out.set(part, p);
    p += part.length;
  }
  out.set(eocd, p);
  return out;
}
